/**
 * Shared utilities + tiny primitives for the /reviews pages.
 * Kept in one place so each page file stays focused on layout, not helpers.
 */
import React from 'react';
import { Warning, CheckCircle, Clock, HourglassMedium } from '@phosphor-icons/react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Compact INR ("₹1.2Cr" / "₹45.6L" / "₹75,000"). Falls back to '—' on NaN. */
export function inr(v, { compact = true } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (compact) {
    const abs = Math.abs(n);
    if (abs >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(abs >= 10_00_00_000 ? 0 : 1)}Cr`;
    if (abs >= 1_00_000)   return `₹${(n / 1_00_000).toFixed(abs >= 10_00_000 ? 0 : 1)}L`;
  }
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

/** "Mon, 15 Jul 2026" — shared date format across the review center. */
export const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
export const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** Live status badge — colour + icon tied to state so operators can scan a
 *  list of periods and instantly see which one needs attention. */
export function PeriodStatusPill({ status, small }) {
  const cfg = {
    upcoming:         { icon: HourglassMedium, cls: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Upcoming' },
    commitment_open:  { icon: Clock,           cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Commit Now' },
    in_progress:      { icon: Warning,         cls: 'bg-blue-100 text-blue-700 border-blue-200',   label: 'Awaiting Review' },
    closed:           { icon: CheckCircle,     cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Closed' },
  };
  const c = cfg[status] || cfg.upcoming;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${c.cls} ${small ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'}`} data-testid={`period-status-${status}`}>
      <Icon size={small ? 10 : 12} weight="fill" /> {c.label}
    </span>
  );
}

/** Approval status pill for a commitment row (pending / approved / rejected /
 *  not-required). Only shown when relevant. */
export function ApprovalPill({ status }) {
  if (!status || status === 'not_required') return null;
  const cfg = {
    pending:  'bg-amber-100 text-amber-700 border-amber-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
  }[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`ml-2 inline-block text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cfg}`}>{status.replace(/_/g, ' ')}</span>;
}

/** Progress bar used on commit page (target coverage). Bright green ≥100%,
 *  amber below, subtle red on very low. */
export function ProgressBar({ pct, testid }) {
  const v = Math.min(200, Math.max(0, Number(pct) || 0));  // cap at 200% for visual
  const color =
    v >= 100 ? 'bg-emerald-500' :
    v >= 75 ? 'bg-lime-500' :
    v >= 40 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="w-full h-2.5 rounded-full bg-[#E2F3F0] overflow-hidden" data-testid={testid}>
      <div className={`h-full ${color} transition-all duration-300`} style={{ width: `${Math.min(100, v)}%` }} />
    </div>
  );
}
